import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import styled from "styled-components";

import HtmlHead from "components/atoms/HtmlHead";
import Icon from "components/atoms/Icon";
import { CountTagItem, TagText } from "components/atoms/Tag";
import Textarea from "components/atoms/Textarea";
import Tooltip from "components/atoms/Tooltip";
import Confirm from "components/atoms/alert/Confirm";
import Button, { BUTTON_STYLES } from "components/atoms/button";
import Text from "components/atoms/text/Text";

import ReferenceBibInfoEditModal from "components/page/modal/ReferenceBibInfoEditModal";
import ReferenceCitationEditModal from "components/page/modal/ReferenceCitationEditModal";
import ReferenceCitationStyleModal from "components/page/modal/ReferenceCitationStyleModal";
import ReferenceFolderEditModal from "components/page/modal/ReferenceFolderEditModal";
import SpacePaymentNewModal from "components/page/modal/SpacePaymentNewModal";
import ReferenceTagEditModal from "components/page/modal/ReferenceTagEditModal";
import PDFChatPageHeader from "components/organisms/review/PDFChatPageHeader";
import PDFChatPageLayout from "components/organisms/review/PDFChatPageLayout";
import PDFChatFileAnalysisView from "components/organisms/review/PDFChatFileAnalysisView";
import PDFChatFileUploadView from "components/organisms/review/PDFChatFileUploadView";
import PDFChatSidebar from "components/organisms/review/PDFChatSidebar";
import PDFJsReviewViewer from "components/organisms/review/PDFJsReviewViewer";
import SpaceBar from "components/templates/SpaceBar";
import SpaceMenuBar, {
    SIDE_WIDTH_AUTOFOLD,
    SIDE_WIDTH_BASE,
    SIDE_WIDTH_MAX,
    SIDE_WIDTH_MIN,
} from "components/templates/SpaceMenuBar";

import {
    useBibliographies,
    useBibliography,
    useBibliographyDelete,
    useBibliographyUpdate,
    useFolders,
} from "hooks/queries/useBibliographies";
import { useUser } from "hooks/queries/useUser";
import { usePDFChatWebSocket } from "hooks/usePDFChatWebSocket";

import Constants from "utils/constants";
import { getDateFromString, getTimeFromString } from "utils/dateUtil";
import { useDevice } from "utils/device";
import { isAdminUser, isReadonlyUser } from "utils/user";
import { genericEnterKeyDown } from "utils/validation";

const DEFAULT_REFERENCE_LIST_PARAMS = {
    page: 1,
    page_size: 100,
    ordering: "-created_at",
};

const PageLayout = styled.div`
    position: relative;
    height: 100vh;
    width: 100vw;
    overflow: hidden;
`;

const ReviewBody = styled.div`
    position: relative;
    display: flex;
    width: 100%;
    height: calc(100vh - ${Constants.TABLET_OR_PC_HEADER_HEIGHT}px);
    overflow: hidden;
    background-color: var(--color-White);
`;

const ReviewMainContainer = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    min-width: 640px;
    overflow: hidden;
    background-color: var(--color-White);
`;

const ReviewMainInner = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-width: 0;
`;

const ReviewViewContent = styled.div`
    flex: 1;
    min-height: 0;
    overflow: hidden;
`;

const PdfReaderContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const ReferenceDetailPreview = styled.div`
    width: 100%;
    height: 100%;
    overflow: auto;
    padding: 24px 28px 64px;
    background-color: #f4f6f7;
`;

const ReferenceDetailPreviewInner = styled.div`
    width: min(100%, 920px);
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const DetailCard = styled.section`
    width: 100%;
    padding: 28px 32px;
    background-color: var(--color-White);
    border: solid 1px #e4e9ed;
    border-radius: 4px;
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    height: 20px;
    gap: 4px;
    margin-bottom: 8px;
`;

const SectionHeaderTitle = styled(Text)`
    font-size: 1.4rem;
    font-weight: 500;
    padding: 0 0 0 8px;
    border-left: solid 2px var(--color-TitleBar);
`;

const BibInfoSection = styled.div`
    margin: 0;
`;

const BibInfoText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-Grey1)",
})`
    display: block;
`;

const MetadataExtractionStatus = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})`
    display: inline-flex;
    align-items: center;
    gap: 6px;
`;

const AbstractSection = styled.div`
    margin: 0;
    min-height: 32px;
`;

const AbstractTextarea = styled(Textarea)``;

const AnnotaionSection = styled.div`
    display: block;
    margin-bottom: 28px;
`;

const AnnotationLeftSection = styled.div`
    width: 100%;
    max-width: none;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 20px;

    @media (max-width: ${Constants.TABLET_WIDTH}px) {
        grid-template-columns: 1fr;
    }
`;

const AnnotationRightSection = styled.div`
    width: 100%;
    max-width: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ReferenceLinkItem = styled.div`
    width: 100%;
    margin-bottom: 0;
`;

const ReferenceLinkItemHeader = styled(SectionHeader)``;

const ReferenceLinkItemHeaderTitle = styled(Text)`
    font-size: 1.4rem;
    font-weight: 500;
    padding: 0 0 0 8px;
    border-left: solid 2px var(--color-TitleBar);
`;

const ReferenceLinkTagItemWrap = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    width: auto;
    min-height: 20px;
`;

const ReferenceLinkTagItem = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: ${(props) => props.$margin || "0"};
    padding: 0px 8px;
    max-width: 180px;
    min-width: 0;
    height: 20px;
    overflow: hidden;
    outline: none;
    background-color: var(--color-Tag);
    border: transparent;
    border-radius: 4px;
`;

const ReferenceLinkItemBlankBox = styled.div`
    display: flex;
    justify-content: start;
    align-items: center;
    height: 20px;
`;

const ReferenceLinkItemBlankText = styled(Text)`
    font-size: 1.2rem;
    color: var(--color-Grey1);
`;

const MemoSection = styled.div`
    display: flex;
    flex-direction: column;
`;

const MemoTitleText = styled(Text).attrs({
    variant: "body-md-500",
})`
    margin: 0 0 8px 0;
    padding: 0 0 0 8px;
    border-left: solid 2px var(--color-TitleBar);
`;

const MemoDiv = styled.div`
    flex: 1;
`;

const MemoTextarea = styled.textarea`
    padding: 4px 6px;
    width: 100%;
    min-height: 100%;
    font-size: 1.2rem;
    font-weight: 400;
    color: var(--color-SubBlack);
    background-color: var(--color-White);
    border: solid 1px #ecf2f4;
    border-radius: 2px;
    resize: none;
    outline: none;

    &::placeholder {
        color: var(--color-Grey2);
    }

    &:disabled {
        background-color: var(--color-DisabledInput);
        cursor: not-allowed;
    }
`;

const DateSection = styled.div`
    flex: 0 0 auto;
    display: flex;
    justify-content: end;
    align-items: center;
    gap: 8px;
    margin-top: 16px;
`;

const DateText = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})``;

const ReferenceListSection = styled.div`
    width: 100%;
`;

const ReferenceListHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    height: 20px;
    margin-bottom: 8px;
`;

const ReferenceListTitleText = styled(SectionHeaderTitle)``;

const ReferenceListParamsWrap = styled.div`
    display: flex;
    align-items: end;
    flex-wrap: wrap;
    margin-bottom: 8px;
`;

const ReferenceFilterTagItem = styled(ReferenceLinkTagItem)`
    transition: all 120ms ease-in;
    background-color: var(--color-Tag);
    cursor: pointer;

    &.active {
        background-color: var(--color-TagActive);
    }
`;

const ReferenceListContent = styled.div``;

const ReferenceListBlankContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 16px;
    margin-top: 80px;
`;

const ReferenceListBlankText = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-SubBlack)",
})``;

const ReferenceListItemRow = styled.div`
    padding: 8px 0px;
    border-bottom: solid 1px var(--color-Background);
    cursor: default;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        transition: all 80ms ease-in;
        cursor: pointer;
        background-color: var(--color-ListHover);
    }
`;

const ReferenceListItemTitle = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-SubBlack)",
})``;

const ReferenceListItemText = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})``;

const ThreadListPanel = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background-color: var(--color-White);
`;

const LeftPanelSection = styled.section`
    display: flex;
    flex-direction: column;
`;

const LeftPanelSectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-height: 36px;
    padding: 12px 12px 6px 12px;
`;

const LeftPanelSectionTitle = styled(Text).attrs({
    variant: "body-lg-500",
    color: "var(--color-SubBlack)",
})``;

const LeftPanelSectionActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin-left: auto;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background-color: transparent;
    cursor: pointer;

    &:hover {
        background-color: var(--color-ButtonHover4);
    }
`;

const LeftPanelStatusSection = styled(LeftPanelSection)`
    flex: 0 0 auto;
    padding-bottom: 6px;
    border-bottom: solid 1px var(--color-Outline);
`;

const ThreadListSection = styled(LeftPanelSection)`
    flex: 1;
    min-height: 0;
`;

const LeftPanelViewButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    min-width: 0;
    height: 24px;
    padding: 0 12px;
    border: 0;
    border-radius: 999px;
    background-color: ${({ $active }) => ($active ? "var(--color-Key)" : "var(--color-White)")};
    color: ${({ $active }) => ($active ? "var(--color-White)" : "var(--color-Key)")};
    font-size: 1.2rem;
    font-weight: 700;
    cursor: pointer;
    line-height: 1;
    white-space: nowrap;

    &:hover {
        cursor: pointer;
    }
`;

const LeftPanelViewToggle = styled.div`
    display: flex;
    align-items: center;
    width: max-content;
    margin-left: auto;
    padding: 2px;
    background-color: var(--color-White);
    flex-shrink: 0;
`;

const ThreadListBody = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 4px 0 12px 0;
`;

const ThreadListItem = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    min-height: 32px;
    margin: 2px 8px;
    padding: 6px 10px 6px 12px;
    border: 0;
    border-radius: 6px;
    background-color: ${({ $active }) => ($active ? "var(--color-Base2)" : "transparent")};
    cursor: pointer;

    &:hover {
        background-color: ${({ $active }) => ($active ? "var(--color-Base2)" : "var(--color-ListHover)")};
    }

    &:hover .thread-list-item-more,
    &:focus-within .thread-list-item-more {
        opacity: 1;
        pointer-events: auto;
    }

    &:hover .thread-list-item-title,
    &:focus-within .thread-list-item-title {
        padding-right: 28px;
    }

    ${({ $menuOpen }) =>
        $menuOpen
            ? `
        .thread-list-item-title {
            padding-right: 28px;
        }
    `
            : ""}
`;

const ThreadListItemTitle = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-SubBlack)",
})`
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: padding-right 100ms ease-in;
`;

const ThreadListItemTitleInput = styled.input`
    flex: 1;
    min-width: 0;
    height: 24px;
    padding: 0 6px;
    border: solid 1px var(--color-Key);
    border-radius: 3px;
    outline: none;
    font-size: 1.3rem;
    color: var(--color-SubBlack);
`;

const ThreadListItemMoreButton = styled.button`
    position: absolute;
    top: 50%;
    right: 0px;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background-color: transparent;
    cursor: pointer;
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
    transition: opacity 100ms ease-in;

    &:hover {
        background-color: var(--color-ButtonHover4);
    }
`;

const ThreadListContextMenu = styled.div`
    position: fixed;
    top: ${({ $top }) => $top}px;
    left: ${({ $left }) => $left}px;
    z-index: 1000;
    width: 128px;
    padding: 4px;
    border: solid 1px var(--color-Outline);
    border-radius: 4px;
    background-color: var(--color-White);
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.14);
`;

const ThreadListContextMenuItem = styled.button`
    display: flex;
    align-items: center;
    width: 100%;
    height: 28px;
    padding: 0 8px;
    border: 0;
    border-radius: 3px;
    background-color: transparent;
    color: ${({ $danger }) => ($danger ? "var(--color-Warn)" : "var(--color-SubBlack)")};
    font-size: 1.2rem;
    text-align: left;
    cursor: pointer;

    &:hover {
        background-color: var(--color-ListHover);
    }
`;

const ThreadListEmptyText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-Grey1)",
})`
    padding: 24px 12px;
    text-align: center;
`;

const ThreadListLoadingRow = styled(ThreadListEmptyText)`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
`;

function ReferenceDetailPage(props) {
    // 라우터 파라미터
    const { space_uuid, reference_no } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const referenceNo = Number(reference_no);
    const device = useDevice();
    const sidebarDataKey = "sidebarData";
    const sidebarData =
        typeof window === "undefined" ? null : JSON.parse(localStorage.getItem(sidebarDataKey) || "null");
    const initialSidebarFold = sidebarData?.f || false;
    const initialSidebarWidth = initialSidebarFold
        ? SIDE_WIDTH_MIN
        : Math.max(SIDE_WIDTH_BASE, sidebarData?.w || SIDE_WIDTH_BASE);
    const wBarRef = useRef(null);
    const [isSpaceMenuFold, setIsSpaceMenuFold] = useState(initialSidebarFold);
    const [spaceMenuWidth, setSpaceMenuWidth] = useState(initialSidebarWidth);
    const [spaceMenuResize, setSpaceMenuResize] = useState(false);
    const [spaceMenuHover, setSpaceMenuHover] = useState(false);
    const [activeMainView, setActiveMainView] = useState(searchParams.get("view") === "pdf" ? "pdf" : "metadata");
    const [showPaidPlanSubscribeModal, setShowPaidPlanSubscribeModal] = useState(false);

    // 사용자 데이터
    const [user, setUser] = useState({});
    const userQuery = useUser();

    useEffect(() => {
        if (userQuery.data) {
            setUser(userQuery.data);
        }
    }, [userQuery.data]);

    const volume = user?.current_space?.volume_info;
    const isVolumeWarn = ((volume?.used / volume?.max) * 100).toFixed(0) >= 90;
    const formatBytes = (bytes, decimals = 2) => {
        if (!bytes) return "0 Bytes";
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    };

    // 레퍼런스 데이터
    const referenceQuery = useBibliography({ reference_no: referenceNo });
    const [reference, setReference] = useState({});
    useEffect(() => {
        if (referenceQuery.data) {
            setReference(referenceQuery.data);
        }
    }, [referenceQuery.data]);

    useEffect(() => {
        if (!Number.isInteger(referenceNo) || referenceNo < 1) {
            navigate("/error", { replace: true });
        }
    }, [referenceNo, navigate]);

    const updateBibliographyMutation = useBibliographyUpdate();
    const deleteBibliographyMutation = useBibliographyDelete();
    const abstractTextareaRef = useRef(null);
    const memoTextareaRef = useRef();
    const [showCitationStyleModal, setShowCitationStyleModal] = useState(false);
    const [showFolderEditModal, setShowFolderEditModal] = useState(false);
    const [showTagEditModal, setShowTagEditModal] = useState(false);
    const [showCitationEditModal, setShowCitationEditModal] = useState(false);
    const [showBibInfoEditModal, setShowBibInfoEditModal] = useState(false);

    const handleReferenceFieldChanged = useCallback((event) => {
        const { name, value } = event.target;
        // bib_json 내부 필드는 "bib_json.필드명" 형태로 name을 넣어줄 수 있음
        if (name.startsWith("bib_json.")) {
            setReference((prev) => ({
                ...prev,
                bib_json: {
                    ...prev.bib_json,
                    [name.replace("bib_json.", "")]: value,
                },
            }));
            return;
        }
        setReference((prev) => ({
            ...prev,
            [name]: value,
        }));
    }, []);

    const updateReferenceField = useCallback(
        (fields) => {
            if (!reference.id) return;
            const updateData = { id: reference.id };
            Object.entries(fields).forEach(([key, value]) => {
                if (key.startsWith("bib_json.")) {
                    if (!updateData.bib_json) updateData.bib_json = { ...reference.bib_json };
                    updateData.bib_json[key.replace("bib_json.", "")] = value;
                    return;
                }
                updateData[key] = value;
            });
            updateBibliographyMutation.mutate(updateData);
        },
        [reference.bib_json, reference.id, updateBibliographyMutation],
    );

    const handleDeleteButtonClick = useCallback(() => {
        let message = "참고문헌을 삭제하시겠습니까?";
        if (reference.citation_count > 0) {
            message = "연구노트에 인용된 참고문헌입니다. 삭제하시겠습니까?";
        }
        Confirm("warn", "참고문헌 삭제", message, "삭제", () => {
            deleteBibliographyMutation.mutate({
                ids: [reference.id],
            });
            navigate(`/${space_uuid}/reference`);
        });
    }, [deleteBibliographyMutation, navigate, reference.citation_count, reference.id, space_uuid]);

    // 폴더
    const folderQuery = useFolders();
    const [referenceFolder, setReferenceFolder] = useState(null);
    useEffect(() => {
        if (folderQuery.data || reference.folder) {
            setReferenceFolder(folderQuery.data?.find((folder) => folder.id === reference.folder) || null);
        }
    }, [folderQuery.data, reference.folder]);

    useEffect(() => {
        const textarea = memoTextareaRef.current;
        if (!textarea) return;
        textarea.style.height = "auto";
        textarea.style.height = `${textarea.scrollHeight}px`;
    }, [reference?.memo]);

    const [referenceList, setReferenceList] = useState([]);
    const [referenceListParams, setReferenceListParams] = useState(null);
    const previousReferenceFilterRef = useRef(null);
    const referenceListQuery = useBibliographies(referenceListParams);
    const { data: referenceListData, refetch: refetchReferenceList } = referenceListQuery;

    useEffect(() => {
        if (reference?.id && referenceListParams === null) {
            setReferenceListParams({
                ...DEFAULT_REFERENCE_LIST_PARAMS,
                folder: reference.folder,
                tags: reference.tag_mappings?.map((tag) => tag.id).join(",") || "",
                researches: reference.researches?.map((research) => research.id).join(",") || "",
            });
        }
    }, [reference, referenceListParams]);

    useEffect(() => {
        if (!reference?.id) return;

        const currentFilter = {
            referenceId: reference.id,
            folder: reference.folder || null,
            tags: reference.tag_mappings?.map((tag) => tag.id) || [],
            researches: reference.researches?.map((research) => research.id) || [],
        };
        const previousFilter = previousReferenceFilterRef.current;

        if (!previousFilter || previousFilter.referenceId !== reference.id || referenceListParams === null) {
            previousReferenceFilterRef.current = currentFilter;
            return;
        }

        const previousTagSet = new Set(previousFilter.tags);
        const currentTagSet = new Set(currentFilter.tags);
        const previousResearchSet = new Set(previousFilter.researches);
        const currentResearchSet = new Set(currentFilter.researches);

        const addedTagIds = currentFilter.tags.filter((tagId) => !previousTagSet.has(tagId));
        const removedTagIds = previousFilter.tags.filter((tagId) => !currentTagSet.has(tagId));
        const addedResearchIds = currentFilter.researches.filter((researchId) => !previousResearchSet.has(researchId));
        const removedResearchIds = previousFilter.researches.filter(
            (researchId) => !currentResearchSet.has(researchId),
        );
        const folderChanged = previousFilter.folder !== currentFilter.folder;

        if (
            folderChanged ||
            addedTagIds.length > 0 ||
            removedTagIds.length > 0 ||
            addedResearchIds.length > 0 ||
            removedResearchIds.length > 0
        ) {
            setReferenceListParams((prev) => {
                if (!prev) return prev;

                const nextTagIds = new Set(
                    (prev.tags || "")
                        .split(",")
                        .filter((tagId) => !!tagId)
                        .map((tagId) => Number(tagId)),
                );
                removedTagIds.forEach((tagId) => nextTagIds.delete(tagId));
                addedTagIds.forEach((tagId) => nextTagIds.add(tagId));

                const nextResearchIds = new Set(
                    (prev.researches || "")
                        .split(",")
                        .filter((researchId) => !!researchId)
                        .map((researchId) => Number(researchId)),
                );
                removedResearchIds.forEach((researchId) => nextResearchIds.delete(researchId));
                addedResearchIds.forEach((researchId) => nextResearchIds.add(researchId));

                return {
                    ...prev,
                    folder: folderChanged ? currentFilter.folder : prev.folder,
                    tags: Array.from(nextTagIds).join(","),
                    researches: Array.from(nextResearchIds).join(","),
                };
            });
        }

        previousReferenceFilterRef.current = currentFilter;
    }, [reference, referenceListParams]);

    useEffect(() => {
        if (referenceListParams) {
            refetchReferenceList();
        }
    }, [referenceListParams, refetchReferenceList]);

    useEffect(() => {
        if (referenceListData) {
            setReferenceList(referenceListData.results || []);
        }
    }, [referenceListData]);

    const persistSpaceMenuData = useCallback(
        (nextWidth = spaceMenuWidth, nextFold = isSpaceMenuFold) => {
            localStorage.setItem(sidebarDataKey, JSON.stringify({ w: nextWidth, f: nextFold }));
        },
        [isSpaceMenuFold, sidebarDataKey, spaceMenuWidth],
    );

    const toggleSpaceMenu = useCallback(
        (event) => {
            event?.stopPropagation?.();
            if (isSpaceMenuFold) {
                const savedSidebarData = JSON.parse(localStorage.getItem(sidebarDataKey) || "null");
                const nextWidth = Math.max(SIDE_WIDTH_BASE, savedSidebarData?.w || SIDE_WIDTH_BASE);
                setIsSpaceMenuFold(false);
                setSpaceMenuWidth(nextWidth);
                persistSpaceMenuData(nextWidth, false);
                return;
            }
            setIsSpaceMenuFold(true);
            setSpaceMenuWidth(SIDE_WIDTH_MIN);
            persistSpaceMenuData(spaceMenuWidth, true);
        },
        [isSpaceMenuFold, persistSpaceMenuData, sidebarDataKey, spaceMenuWidth],
    );

    const handleBackButtonClick = useCallback(() => {
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }
        navigate(`/${space_uuid}/reference`);
    }, [navigate, space_uuid]);

    const startSpaceMenuResizing = useCallback(() => {
        if (isSpaceMenuFold) return;
        setSpaceMenuResize(true);
        let latestWidth = spaceMenuWidth;

        const applySpaceMenuResizing = (event) => {
            if (!wBarRef.current) return;
            const newWidth = event.clientX - wBarRef.current.getBoundingClientRect().left;
            latestWidth = Math.min(Math.max(newWidth, SIDE_WIDTH_MIN), SIDE_WIDTH_MAX);
            setSpaceMenuWidth(latestWidth);
        };

        const stopSpaceMenuResizing = () => {
            setSpaceMenuResize(false);
            if (latestWidth <= SIDE_WIDTH_AUTOFOLD) {
                setIsSpaceMenuFold(true);
                setSpaceMenuWidth(SIDE_WIDTH_MIN);
                persistSpaceMenuData(latestWidth, true);
            } else {
                persistSpaceMenuData(latestWidth, false);
            }
            window.removeEventListener("mousemove", applySpaceMenuResizing);
            window.removeEventListener("mouseup", stopSpaceMenuResizing);
        };

        window.addEventListener("mousemove", applySpaceMenuResizing);
        window.addEventListener("mouseup", stopSpaceMenuResizing);
    }, [isSpaceMenuFold, persistSpaceMenuData, spaceMenuWidth]);

    // region: page 이동 처리
    const [curPage, setCurPage] = useState(() => Math.max(1, Number(searchParams.get("page")) || 1));
    const [maxPage, setMaxPage] = useState(1);

    const handleInputPageChanged = useCallback(
        (newPage) => {
            if (newPage < 1) newPage = 1;
            if (maxPage > 1 && newPage > maxPage) newPage = maxPage;
            if (newPage === curPage) return;

            setCurPage(newPage);
        },
        [curPage, maxPage],
    );

    // region: 리뷰 박스 처리
    const [boxes, setBoxes] = useState([]);
    const [selectedBox, setSelectedBox] = useState(null);
    const [searchBox, setSearchBox] = useState(null);

    // region: 웹소켓 훅
    const {
        threads,
        isThreadListLoading,
        selectedThread,
        setSelectedThread,
        profiles,
        streamingMessageIds,
        autoScrollToEnd,
        setAutoScrollToEnd,
        pdfIndexingInfo,
        pdfMetadataStatus,
        chatStart,
        chatStop,
        chatDelete,
        threadUpdateTitle,
        myProfileId,
    } = usePDFChatWebSocket({
        reference_uuid: reference.uuid,
        user,
        connectionDependencies: [reference.uuid, userQuery.data],
        onFileUploaded: () => {
            referenceQuery.refetch();
        },
        onFileDeleted: () => {
            referenceQuery.refetch();
            navigate(`/${space_uuid}/reference/${reference_no}`, { replace: true });
        },
        onPdfMetadata: () => {
            referenceQuery.refetch();
        },
        onCreateReviewBox: (review_selection_data) => {
            if (review_selection_data.page === curPage) {
                setBoxes((prevBoxes) => [
                    ...prevBoxes.filter((box) => box.id !== Constants.REVIEW_NEW_SELECTION_ID),
                    { ...review_selection_data },
                ]);
            }
        },
        onStreamStart: (streaming_message_id) => {
            setFocusedMessageId(streaming_message_id);
        },
    });

    useEffect(() => {
        const reviewSelections = new Map();
        threads
            .filter((thread) => !thread.is_deleted)
            .forEach((thread) => {
                thread.messages
                    .filter((message) => message.is_deleted === false && message.review_selection)
                    .forEach((message) => {
                        reviewSelections.set(message.review_selection.id, message.review_selection);
                    });
            });

        setBoxes((prevBoxes) => [
            ...Array.from(reviewSelections.values()),
            ...prevBoxes.filter((box) => box.id === Constants.REVIEW_NEW_SELECTION_ID),
        ]);
    }, [threads]);

    const selectedThreadLastVisibleMessageId = useMemo(() => {
        if (!selectedThread?.messages?.length) return null;

        const messageMapByParent = {};
        selectedThread.messages.forEach((message) => {
            const parent = message.parent || null;
            if (!messageMapByParent[parent]) {
                messageMapByParent[parent] = [];
            }
            messageMapByParent[parent].push(message);
        });

        let lastVisibleMessageId = null;
        let parentId = null;
        while (true) {
            const children = messageMapByParent[parentId];
            if (!children || children.length === 0) break;

            const message = children[children.length - 1];
            if (message.is_deleted) break;

            lastVisibleMessageId = message.id;
            parentId = message.id;
        }

        return lastVisibleMessageId;
    }, [selectedThread]);

    // region: 새 창으로 리뷰 진입 처리
    const [pendingThreadId, setPendingThreadId] = useState(() => Number(searchParams.get("thread")) || null);
    const [pendingMessageId, setPendingMessageId] = useState(() => Number(searchParams.get("message")) || null);
    const [focusedMessageId, setFocusedMessageId] = useState(null);
    const [newChatRequestKey, setNewChatRequestKey] = useState(0);
    const [threadActionMenu, setThreadActionMenu] = useState(null);
    const [editingThreadId, setEditingThreadId] = useState(null);
    const [editingThreadTitle, setEditingThreadTitle] = useState("");
    const threadActionMenuRef = useRef(null);
    useEffect(() => {
        if (!pendingThreadId || !threads || !threads.length) return;
        const found = threads.find((t) => t.id === pendingThreadId);
        if (found) {
            setSelectedThread(found);
            setFocusedMessageId(pendingMessageId);
            setPendingThreadId(null);
            setPendingMessageId(null);
        }
    }, [pendingMessageId, pendingThreadId, setSelectedThread, threads]);

    useEffect(() => {
        if (!threadActionMenu) return;

        const closeMenu = (event) => {
            if (threadActionMenuRef.current?.contains(event.target)) return;
            setThreadActionMenu(null);
        };

        window.addEventListener("mousedown", closeMenu);
        return () => window.removeEventListener("mousedown", closeMenu);
    }, [threadActionMenu]);

    const commitThreadTitleEdit = useCallback(
        (thread) => {
            const nextTitle = editingThreadTitle.trim();
            setEditingThreadId(null);
            setEditingThreadTitle("");

            if (!nextTitle || nextTitle === thread.title) return;
            threadUpdateTitle(thread.id, nextTitle);
        },
        [editingThreadTitle, threadUpdateTitle],
    );

    const handleStartNewChat = useCallback(() => {
        setSelectedThread(null);
        setSelectedBox(null);
        setSearchBox(null);
        setFocusedMessageId(null);
        setThreadActionMenu(null);
        setNewChatRequestKey((prev) => prev + 1);
    }, [setSelectedThread]);

    const handleSelectReviewMessage = useCallback(
        (message) => {
            if (!message?.id) return;

            const targetThread = threads
                .filter((thread) => !thread.is_deleted)
                .find((thread) => thread.messages.some((threadMessage) => threadMessage.id === message.id));

            if (!targetThread) return;

            setSelectedBox(null);
            setSearchBox(null);
            setSelectedThread(targetThread);
            setFocusedMessageId(null);
            requestAnimationFrame(() => setFocusedMessageId(message.id));
        },
        [setSelectedThread, threads],
    );

    const renderThreadTitle = (title) => (
        <span
            dangerouslySetInnerHTML={{
                __html: (title || "").replace(
                    /@\[([^\]]+)\]\(([^)]+)\)/g,
                    `<span style="color: green; font-size: 1.3rem; display: inline-block;">@$1</span>`,
                ),
            }}
        />
    );

    const referenceListParamsRenderer = () => {
        const paramELs = [];
        if (referenceList && referenceListParams && referenceFolder) {
            const isActive = referenceFolder.id === referenceListParams.folder;
            paramELs.push(
                <Tooltip key="folder" message={isActive ? "클릭하여 필터 해제" : "클릭하여 필터 적용"}>
                    <ReferenceFilterTagItem
                        className={isActive ? "active" : ""}
                        $margin={"0 8px 2px 0"}
                        onClick={() => {
                            if (isActive) {
                                setReferenceListParams((prev) => ({ ...prev, folder: null }));
                            } else if (referenceListParams.folder === null) {
                                setReferenceListParams((prev) => ({ ...prev, folder: referenceFolder.id }));
                            }
                        }}
                    >
                        <Icon name="folder" size="8" color="var(--color-SubBlack)" />
                        <TagText style={{ margin: "0 8px 0 8px" }}>
                            {referenceFolder.name} (
                            {referenceList.filter((r) => r.folder === referenceFolder.id).length})
                        </TagText>
                        <Icon name={isActive ? "x" : "plus"} size="8" color="var(--color-SubBlack)" />
                    </ReferenceFilterTagItem>
                </Tooltip>,
            );
        }

        if (referenceList && referenceListParams && reference.tag_mappings) {
            const tags = (referenceListParams.tags || "")
                .split(",")
                .filter((tagId) => !!tagId)
                .map((tagId) => reference.tag_mappings.find((item) => item.id === Number(tagId)))
                .filter(Boolean);

            paramELs.push(
                ...reference.tag_mappings.map((tag, index) => {
                    const tagIds = tags.map((item) => item.id);
                    const isActive = tagIds.includes(tag.id);
                    return (
                        <Tooltip
                            key={index + "TAG_FILTER"}
                            message={isActive ? "클릭하여 필터 해제" : "클릭하여 필터 적용"}
                        >
                            <ReferenceFilterTagItem
                                className={isActive ? "active" : ""}
                                $margin={"0 8px 2px 0"}
                                onClick={() => {
                                    if (isActive) {
                                        setReferenceListParams((prev) => ({
                                            ...prev,
                                            tags: prev.tags
                                                .split(",")
                                                .filter((id) => Number(id) !== tag.id)
                                                .join(","),
                                        }));
                                    } else {
                                        tagIds.push(tag.id);
                                        setReferenceListParams((prev) => ({ ...prev, tags: tagIds.join(",") }));
                                    }
                                }}
                            >
                                <Icon name="tag" size="8" color="var(--color-SubBlack)" />
                                <TagText style={{ margin: "0 8px 0 8px" }}>
                                    {tag.name} (
                                    {
                                        referenceList.filter((r) => r.tag_mappings.map((t) => t.id).includes(tag.id))
                                            .length
                                    }
                                    )
                                </TagText>
                                <Icon name={isActive ? "x" : "plus"} size="8" color="var(--color-SubBlack)" />
                            </ReferenceFilterTagItem>
                        </Tooltip>
                    );
                }),
            );
        }

        if (referenceList && referenceListParams && reference.researches) {
            const researches = (referenceListParams.researches || "")
                .split(",")
                .filter((researchId) => !!researchId)
                .map((researchId) => reference.researches.find((item) => item.id === Number(researchId)))
                .filter(Boolean);

            paramELs.push(
                ...reference.researches.map((research, index) => {
                    const researchIds = researches.map((item) => item.id);
                    const isActive = researchIds.includes(research.id);
                    return (
                        <Tooltip
                            key={index + "RESEARCH_FILTER"}
                            message={isActive ? "클릭하여 필터 해제" : "클릭하여 필터 적용"}
                        >
                            <ReferenceFilterTagItem
                                className={isActive ? "active" : ""}
                                $margin={"0 8px 2px 0"}
                                onClick={() => {
                                    if (isActive) {
                                        setReferenceListParams((prev) => ({
                                            ...prev,
                                            researches: prev.researches
                                                .split(",")
                                                .filter((id) => Number(id) !== research.id)
                                                .join(","),
                                        }));
                                    } else {
                                        researchIds.push(String(research.id));
                                        setReferenceListParams((prev) => ({
                                            ...prev,
                                            researches: researchIds.join(","),
                                        }));
                                    }
                                }}
                            >
                                <Icon name="tag" size="8" color="var(--color-SubBlack)" />
                                <TagText style={{ margin: "0 8px 0 8px" }}>
                                    {research.display_name} (
                                    {
                                        referenceList.filter((r) =>
                                            r.researches.map((item) => item.id).includes(research.id),
                                        ).length
                                    }
                                    )
                                </TagText>
                                <Icon name={isActive ? "x" : "plus"} size="8" color="var(--color-SubBlack)" />
                            </ReferenceFilterTagItem>
                        </Tooltip>
                    );
                }),
            );
        }

        return paramELs;
    };

    const renderViewToggle = () => (
        <LeftPanelViewToggle>
            <LeftPanelViewButton
                type="button"
                $active={activeMainView === "metadata"}
                onClick={() => setActiveMainView("metadata")}
            >
                서지정보
            </LeftPanelViewButton>
            <LeftPanelViewButton
                type="button"
                $active={activeMainView === "pdf"}
                onClick={() => setActiveMainView("pdf")}
            >
                PDF
            </LeftPanelViewButton>
        </LeftPanelViewToggle>
    );

    const renderThreadListPanel = () => (
        <ThreadListPanel>
            <LeftPanelStatusSection>
                <LeftPanelSectionHeader>
                    <Icon name="pdf" size="18" color="var(--color-SubBlack)" />
                    <LeftPanelSectionTitle>PDF</LeftPanelSectionTitle>
                </LeftPanelSectionHeader>
                <PDFChatFileAnalysisView
                    reference={reference}
                    pdfIndexingInfo={pdfIndexingInfo}
                    chatStart={chatStart}
                    user={user}
                />
            </LeftPanelStatusSection>
            <ThreadListSection>
                <LeftPanelSectionHeader>
                    <Icon name="thread" size="18" color="var(--color-SubBlack)" />
                    <LeftPanelSectionTitle>채팅</LeftPanelSectionTitle>
                    <Tooltip message="새 채팅">
                        <LeftPanelSectionActionButton type="button" onClick={handleStartNewChat}>
                            <Icon name="plus" size="12" color="var(--color-SubBlack)" />
                        </LeftPanelSectionActionButton>
                    </Tooltip>
                </LeftPanelSectionHeader>
                <ThreadListBody>
                    {isThreadListLoading ? (
                        <ThreadListLoadingRow>
                            <Icon name="load" size="12" color="var(--color-Grey1)" />
                            채팅 목록을 불러오는 중입니다.
                        </ThreadListLoadingRow>
                    ) : threads.filter((thread) => !thread.is_deleted).length === 0 ? (
                        <ThreadListEmptyText>채팅 기록이 없습니다.</ThreadListEmptyText>
                    ) : (
                        threads.map((thread) => {
                            if (thread.is_deleted) return null;
                            return (
                                <ThreadListItem
                                    key={thread.id}
                                    $active={selectedThread?.id === thread.id}
                                    $menuOpen={threadActionMenu?.thread?.id === thread.id}
                                    onClick={() => setSelectedThread(thread)}
                                >
                                    {editingThreadId === thread.id ? (
                                        <ThreadListItemTitleInput
                                            autoFocus
                                            value={editingThreadTitle}
                                            onClick={(event) => event.stopPropagation()}
                                            onChange={(event) => setEditingThreadTitle(event.target.value)}
                                            onBlur={() => commitThreadTitleEdit(thread)}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") {
                                                    commitThreadTitleEdit(thread);
                                                }
                                                if (event.key === "Escape") {
                                                    setEditingThreadId(null);
                                                    setEditingThreadTitle("");
                                                }
                                            }}
                                        />
                                    ) : (
                                        <ThreadListItemTitle className="thread-list-item-title">
                                            {renderThreadTitle(thread.title)}
                                        </ThreadListItemTitle>
                                    )}
                                    {editingThreadId !== thread.id && (
                                        <Tooltip message="더보기">
                                            <ThreadListItemMoreButton
                                                type="button"
                                                className="thread-list-item-more"
                                                $visible={threadActionMenu?.thread?.id === thread.id}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    const rect = event.currentTarget.getBoundingClientRect();
                                                    setThreadActionMenu({
                                                        thread,
                                                        top: rect.bottom + 4,
                                                        left: rect.right - 128,
                                                    });
                                                }}
                                            >
                                                <Icon name="submenu" size="14" color="var(--color-Grey1)" />
                                            </ThreadListItemMoreButton>
                                        </Tooltip>
                                    )}
                                </ThreadListItem>
                            );
                        })
                    )}
                </ThreadListBody>
            </ThreadListSection>
            {threadActionMenu && (
                <ThreadListContextMenu
                    ref={threadActionMenuRef}
                    $top={threadActionMenu.top}
                    $left={threadActionMenu.left}
                >
                    <ThreadListContextMenuItem
                        type="button"
                        onClick={() => {
                            setEditingThreadId(threadActionMenu.thread.id);
                            setEditingThreadTitle(threadActionMenu.thread.title || "");
                            setThreadActionMenu(null);
                        }}
                    >
                        제목 수정
                    </ThreadListContextMenuItem>
                    <ThreadListContextMenuItem
                        type="button"
                        $danger
                        onClick={() => {
                            const targetThread = threadActionMenu.thread;
                            setThreadActionMenu(null);
                            Confirm(
                                "warn",
                                "스레드 삭제",
                                "채팅 스레드를 삭제하면 되돌릴 수 없습니다. 삭제하시겠습니까?",
                                "삭제",
                                () => {
                                    chatDelete(targetThread.id, null, true);
                                },
                            );
                        }}
                    >
                        채팅 삭제
                    </ThreadListContextMenuItem>
                </ThreadListContextMenu>
            )}
        </ThreadListPanel>
    );

    const renderReferenceDetailPreview = () => (
        <ReferenceDetailPreview>
            <ReferenceDetailPreviewInner>
                <DetailCard>
                    <BibInfoSection>
                        <SectionHeader>
                            <SectionHeaderTitle>서지정보</SectionHeaderTitle>
                            <Tooltip message={"서지정보 수정"}>
                                <Button
                                    {...BUTTON_STYLES.iconGhost}
                                    buttonSize={"20px"}
                                    onClick={() => setShowBibInfoEditModal(true)}
                                    disabled={isReadonlyUser(user)}
                                >
                                    <Icon name={"modify"} color={"var(--color-SubBlack)"} />
                                </Button>
                            </Tooltip>
                            <Tooltip message={"인용 스타일"}>
                                <Button
                                    {...BUTTON_STYLES.iconGhost}
                                    buttonSize={"20px"}
                                    onClick={() => setShowCitationStyleModal(true)}
                                >
                                    <Icon name={"list"} color={"var(--color-SubBlack)"} />
                                </Button>
                            </Tooltip>
                        </SectionHeader>
                        {pdfMetadataStatus === "RUNNING" ? (
                            <MetadataExtractionStatus>
                                <Icon name="load" size="12" color="var(--color-Grey1)" />
                                PDF 파일에서 서지정보를 추출 중입니다.
                            </MetadataExtractionStatus>
                        ) : (
                            <BibInfoText>{reference?.styled_text || ""}</BibInfoText>
                        )}
                        <DateSection>
                            <DateText>
                                추가 {getDateFromString(reference.created_at)} {getTimeFromString(reference.created_at)}
                            </DateText>
                            <DateText>|</DateText>
                            <DateText>
                                최근 활동 {getDateFromString(reference.updated_at)}{" "}
                                {getTimeFromString(reference.updated_at)}
                            </DateText>
                        </DateSection>
                    </BibInfoSection>
                </DetailCard>

                <DetailCard>
                    <AbstractSection>
                        <SectionHeader>
                            <SectionHeaderTitle>초록</SectionHeaderTitle>
                        </SectionHeader>
                        {pdfMetadataStatus === "RUNNING" ? (
                            <MetadataExtractionStatus>
                                <Icon name="load" size="12" color="var(--color-Grey1)" />
                                PDF 파일에서 초록을 추출 중입니다.
                            </MetadataExtractionStatus>
                        ) : (
                            <AbstractTextarea
                                ref={abstractTextareaRef}
                                placeholder={"PDF 파일을 업로드하여 자동으로 추출할 수 있습니다."}
                                name={"bib_json.abstract"}
                                value={reference?.bib_json?.abstract || ""}
                                onChange={handleReferenceFieldChanged}
                                saveAct={() =>
                                    updateReferenceField({
                                        "bib_json.abstract": reference?.bib_json?.abstract || "",
                                    })
                                }
                                disabled={isReadonlyUser(user)}
                                padding={"2px"}
                            />
                        )}
                    </AbstractSection>
                </DetailCard>

                <DetailCard>
                    <AnnotationRightSection>
                        <MemoSection>
                            <MemoTitleText>메모</MemoTitleText>
                            <MemoDiv>
                                <MemoTextarea
                                    ref={memoTextareaRef}
                                    rows={1}
                                    name={"memo"}
                                    placeholder={"입력된 메모가 없습니다."}
                                    value={reference?.memo || ""}
                                    onChange={handleReferenceFieldChanged}
                                    onKeyDown={(event) => {
                                        genericEnterKeyDown(event, () => {
                                            if (event.shiftKey) {
                                                handleReferenceFieldChanged(event);
                                            } else {
                                                event.preventDefault();
                                                memoTextareaRef.current.blur();
                                            }
                                        });
                                    }}
                                    onBlur={() => {
                                        updateReferenceField({ memo: reference.memo });
                                    }}
                                    disabled={isReadonlyUser(user)}
                                />
                            </MemoDiv>
                        </MemoSection>
                    </AnnotationRightSection>
                </DetailCard>

                <DetailCard>
                    <AnnotaionSection>
                        <AnnotationLeftSection>
                            <ReferenceLinkItem>
                                <ReferenceLinkItemHeader>
                                    <ReferenceLinkItemHeaderTitle>폴더</ReferenceLinkItemHeaderTitle>
                                    <Tooltip message={"폴더 연결"}>
                                        <Button
                                            {...BUTTON_STYLES.iconGhost}
                                            buttonSize={"20px"}
                                            onClick={() => setShowFolderEditModal(true)}
                                            disabled={isReadonlyUser(user)}
                                        >
                                            <Icon name={"modify"} color={"var(--color-SubBlack)"} />
                                        </Button>
                                    </Tooltip>
                                </ReferenceLinkItemHeader>
                                {referenceFolder && referenceList ? (
                                    <ReferenceLinkTagItemWrap>
                                        <ReferenceLinkTagItem $margin={"0 8px 2px 0"}>
                                            <TagText>{referenceFolder.name}</TagText>
                                        </ReferenceLinkTagItem>
                                    </ReferenceLinkTagItemWrap>
                                ) : (
                                    <ReferenceLinkItemBlankBox>
                                        <ReferenceLinkItemBlankText>연결된 폴더가 없습니다.</ReferenceLinkItemBlankText>
                                    </ReferenceLinkItemBlankBox>
                                )}
                            </ReferenceLinkItem>
                            <ReferenceLinkItem>
                                <ReferenceLinkItemHeader>
                                    <ReferenceLinkItemHeaderTitle>태그</ReferenceLinkItemHeaderTitle>
                                    <Tooltip message={"태그 연결"}>
                                        <Button
                                            {...BUTTON_STYLES.iconGhost}
                                            buttonSize={"20px"}
                                            onClick={() => setShowTagEditModal(true)}
                                            disabled={isReadonlyUser(user)}
                                        >
                                            <Icon name={"modify"} color={"var(--color-SubBlack)"} />
                                        </Button>
                                    </Tooltip>
                                </ReferenceLinkItemHeader>
                                {reference?.tag_mappings?.length > 0 && referenceList ? (
                                    <ReferenceLinkTagItemWrap>
                                        {reference.tag_mappings.map((tag, index) => (
                                            <ReferenceLinkTagItem key={index + "TAG"} $margin={"0 8px 2px 0"}>
                                                <TagText ellipsis>{tag.name}</TagText>
                                            </ReferenceLinkTagItem>
                                        ))}
                                    </ReferenceLinkTagItemWrap>
                                ) : (
                                    <ReferenceLinkItemBlankBox>
                                        <ReferenceLinkItemBlankText>연결된 태그가 없습니다.</ReferenceLinkItemBlankText>
                                    </ReferenceLinkItemBlankBox>
                                )}
                            </ReferenceLinkItem>
                            <ReferenceLinkItem>
                                <ReferenceLinkItemHeader>
                                    <ReferenceLinkItemHeaderTitle>연구노트</ReferenceLinkItemHeaderTitle>
                                    <Tooltip message={"연구노트 연결"}>
                                        <Button
                                            {...BUTTON_STYLES.iconGhost}
                                            buttonSize={"20px"}
                                            onClick={() => setShowCitationEditModal(true)}
                                            disabled={isReadonlyUser(user)}
                                        >
                                            <Icon name={"modify"} color={"var(--color-SubBlack)"} />
                                        </Button>
                                    </Tooltip>
                                </ReferenceLinkItemHeader>
                                {reference?.researches?.length > 0 && referenceList ? (
                                    <ReferenceLinkTagItemWrap>
                                        {reference.researches.map((research, index) => (
                                            <ReferenceLinkTagItem key={index + "RESEARCH"} $margin={"0 8px 2px 0"}>
                                                <TagText>{research.display_name}</TagText>
                                            </ReferenceLinkTagItem>
                                        ))}
                                    </ReferenceLinkTagItemWrap>
                                ) : (
                                    <ReferenceLinkItemBlankBox>
                                        <ReferenceLinkItemBlankText>
                                            연결된 연구노트가 없습니다.
                                        </ReferenceLinkItemBlankText>
                                    </ReferenceLinkItemBlankBox>
                                )}
                            </ReferenceLinkItem>
                        </AnnotationLeftSection>
                    </AnnotaionSection>

                    <ReferenceListSection>
                        <ReferenceListHeader>
                            <ReferenceListTitleText>연결된 참고문헌</ReferenceListTitleText>
                            <CountTagItem>
                                <TagText>
                                    {referenceListParams?.folder === null &&
                                    referenceListParams?.tags === "" &&
                                    referenceListParams?.researches === ""
                                        ? 0
                                        : referenceList?.length}{" "}
                                    건
                                </TagText>
                            </CountTagItem>
                        </ReferenceListHeader>
                        <ReferenceListParamsWrap>{referenceListParamsRenderer()}</ReferenceListParamsWrap>
                        <ReferenceListContent>
                            {referenceList &&
                                (referenceList.length === 0 ||
                                (referenceListParams?.folder === null &&
                                    referenceListParams?.tags === "" &&
                                    referenceListParams?.researches === "") ? (
                                    <ReferenceListBlankContainer>
                                        <Icon name={"folder"} size={"32"} color={"var(--color-Grey1)"} />
                                        <ReferenceListBlankText>연결된 참고문헌이 없습니다.</ReferenceListBlankText>
                                    </ReferenceListBlankContainer>
                                ) : (
                                    referenceList.map((item, index) => (
                                        <ReferenceListItemRow
                                            key={index}
                                            onClick={() => {
                                                navigate(`/${space_uuid}/reference/${item.reference_no}`);
                                            }}
                                        >
                                            <ReferenceListItemTitle>{item.display_name}</ReferenceListItemTitle>
                                            <ReferenceListItemText>{item.styled_text}</ReferenceListItemText>
                                        </ReferenceListItemRow>
                                    ))
                                ))}
                        </ReferenceListContent>
                    </ReferenceListSection>
                </DetailCard>
            </ReferenceDetailPreviewInner>
        </ReferenceDetailPreview>
    );

    // region: 마크업
    return (
        <PageLayout>
            <HtmlHead title={reference?.display_name} />
            <PDFChatPageHeader
                reference={reference}
                bibliographyMutation={updateBibliographyMutation}
                rightContent={
                    <>
                        <Tooltip message={"즐겨찾기"}>
                            <Button
                                {...BUTTON_STYLES.iconSurfaceStatic}
                                border={
                                    reference?.is_favorite
                                        ? "solid 2px var(--color-Key)"
                                        : "solid 2px var(--color-DisabledButton)"
                                }
                                onClick={() => updateReferenceField({ is_favorite: !reference.is_favorite })}
                                disabled={isReadonlyUser(user)}
                            >
                                <Icon
                                    name={"bookmark"}
                                    size={"12"}
                                    color={reference?.is_favorite ? "var(--color-Key)" : "var(--color-DisabledButton)"}
                                />
                            </Button>
                        </Tooltip>
                        <Tooltip message={"참고문헌 삭제"}>
                            <Button
                                onlyIcon
                                {...BUTTON_STYLES.actionPrimary}
                                onClick={handleDeleteButtonClick}
                                disabled={isReadonlyUser(user) && !isAdminUser(user)}
                            >
                                <Icon name={"delete"} size={"12"} color={"var(--color-White)"} />
                            </Button>
                        </Tooltip>
                    </>
                }
            />
            <ReviewBody>
                <SpaceBar mode="reference-detail" onBack={handleBackButtonClick} />
                <SpaceMenuBar
                    customMode
                    customTitleLabel="참고문헌"
                    customTitle={"상세보기"}
                    customTitleAction={renderViewToggle()}
                    customContent={renderThreadListPanel()}
                    showCustomVolumeSection
                    device={device}
                    wBarRef={wBarRef}
                    isFold={isSpaceMenuFold}
                    wBarSize={spaceMenuWidth}
                    wBarResize={spaceMenuResize}
                    wBarHover={spaceMenuHover}
                    startWBarResizing={startSpaceMenuResizing}
                    setWBarHover={setSpaceMenuHover}
                    toggleSidebar={toggleSpaceMenu}
                    space_uuid={space_uuid}
                    navigate={navigate}
                    user={user}
                    setShowPaidPlanSubscribeModal={setShowPaidPlanSubscribeModal}
                    volume={volume}
                    isVolumeWarn={isVolumeWarn}
                    formatBytes={formatBytes}
                />
                <ReviewMainContainer
                    style={{
                        left: `${Constants.SPACE_BAR_WIDTH + spaceMenuWidth}px`,
                        width: `calc(100% - ${Constants.SPACE_BAR_WIDTH + spaceMenuWidth}px)`,
                    }}
                >
                    <ReviewMainInner>
                        <ReviewViewContent>
                            <PDFChatPageLayout
                                showPanelRails={true}
                                main={
                                    activeMainView === "metadata" ? (
                                        renderReferenceDetailPreview()
                                    ) : !reference?.file ? (
                                        <PdfReaderContainer>
                                            <PDFChatFileUploadView user={user} reference={reference} />
                                        </PdfReaderContainer>
                                    ) : (
                                        <PdfReaderContainer>
                                            <PDFJsReviewViewer
                                                fileUrl={reference.file}
                                                referenceFileUuid={reference.file_uuid}
                                                page={curPage}
                                                maxPage={maxPage}
                                                boxes={boxes}
                                                setBoxes={setBoxes}
                                                threads={threads}
                                                selectedBox={selectedBox}
                                                setSelectedBox={setSelectedBox}
                                                searchBox={searchBox}
                                                setSearchBox={setSearchBox}
                                                setMaxPage={setMaxPage}
                                                onPageChange={handleInputPageChanged}
                                                chatStart={chatStart}
                                                parentMessageId={selectedThreadLastVisibleMessageId}
                                                profiles={profiles}
                                                onSelectReviewMessage={handleSelectReviewMessage}
                                            />
                                        </PdfReaderContainer>
                                    )
                                }
                                sidebar={
                                    <PDFChatSidebar
                                        user={user}
                                        myProfileId={myProfileId}
                                        reference={reference}
                                        threads={threads}
                                        selectedThread={selectedThread}
                                        setSelectedThread={setSelectedThread}
                                        profiles={profiles}
                                        setCurPage={setCurPage}
                                        selectedBox={selectedBox}
                                        setSelectedBox={setSelectedBox}
                                        searchBox={searchBox}
                                        setSearchBox={setSearchBox}
                                        chatStart={chatStart}
                                        chatStop={chatStop}
                                        chatDelete={chatDelete}
                                        threadUpdateTitle={threadUpdateTitle}
                                        streamingMessageIds={streamingMessageIds}
                                        autoScrollToEnd={autoScrollToEnd}
                                        setAutoScrollToEnd={setAutoScrollToEnd}
                                        pdfIndexingInfo={pdfIndexingInfo}
                                        focusedMessageId={focusedMessageId}
                                        newChatRequestKey={newChatRequestKey}
                                        onStartNewChat={handleStartNewChat}
                                    />
                                }
                            ></PDFChatPageLayout>
                        </ReviewViewContent>
                    </ReviewMainInner>
                </ReviewMainContainer>
            </ReviewBody>
            {showCitationStyleModal && (
                <ReferenceCitationStyleModal exitModal={() => setShowCitationStyleModal(false)} reference={reference} />
            )}
            {showCitationEditModal && (
                <ReferenceCitationEditModal
                    exitModal={() => setShowCitationEditModal(false)}
                    citedResearches={reference.researches}
                    referenceId={reference?.id}
                />
            )}
            {showFolderEditModal && (
                <ReferenceFolderEditModal
                    referenceIds={[reference.id]}
                    selectedFolderId={reference.folder}
                    exitModal={() => setShowFolderEditModal(false)}
                />
            )}
            {showTagEditModal && (
                <ReferenceTagEditModal
                    referenceId={reference.id}
                    referenceFile={reference.file}
                    selectedTagIds={reference.tag_mappings?.map((tag) => tag.id) || []}
                    exitModal={() => {
                        setShowTagEditModal(false);
                    }}
                />
            )}
            {showBibInfoEditModal && (
                <ReferenceBibInfoEditModal
                    reference={reference}
                    exitModal={() => {
                        setShowBibInfoEditModal(false);
                    }}
                />
            )}
            {showPaidPlanSubscribeModal && (
                <SpacePaymentNewModal
                    user={user}
                    exitModal={() => setShowPaidPlanSubscribeModal(!showPaidPlanSubscribeModal)}
                />
            )}
        </PageLayout>
    );
}

export default ReferenceDetailPage;
